import {
  getDefaultThumbnail,
  getLink,
  getLink as getCourseLink,
  getLinkPath
} from '@models/Course';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import CourseCard from 'aquilla/core/organisms/CourseCard';
import * as React from 'react';
import pushRoute from '@utils/pushRoute';
import { getProfileCourseCardDetailsLine } from '@comp/PlusCourseCard/util';
import Image from '@base/FutureImage';
import { isFuture, isPast } from 'date-fns';

interface IAuthor {
  username?: string;
  name: string;
  user?: { relative_link?: string };
}

interface NewCourseCardProps {
  course: {
    name: string;
    topic: string;
    language: string;
    coverPhotoV1?: string;
    coverPhoto?: string;
    thumbnail?: string;
    authors: IAuthor[];
    isLive: boolean;
    itemCount: number;
    startsAt: string;
    endsAt: string;
    nextSession: {
      type: 'post' | 'quiz';
      properties: {
        uid: string;
        postThumbnail: string;
        permalink: string;
        relativeLink: string;
        slug: string;
        name: string;
        state: number;
        liveAt: string;
      };
      rank: number;
      contentTypeItemRank: number;
    };
  };
  className?: string;
}

const handleCompletedLessonCount = (
  course: NewCourseCardProps['course']
): number => {
  const courseType = course.nextSession?.type;
  const contentTypeItemRank = course.nextSession?.contentTypeItemRank;
  const rank = course.nextSession?.rank;
  const isFutureCourse = course.startsAt && isFuture(new Date(course.startsAt));
  const isOngoingCourse =
    isPast(new Date(course.startsAt)) && isFuture(new Date(course.endsAt));

  if (isFutureCourse && course.itemCount) return 1;
  if (isOngoingCourse) {
    if (courseType === 'post') {
      // Handle the unexpected case where backend sending the contentTypeItemRank as 0
      if (contentTypeItemRank === 0) return 0;
      return contentTypeItemRank - 1;
    }
    if (courseType === 'quiz') {
      return rank - contentTypeItemRank;
    }
  }
  return 0;
};

export default function NewCourseCard(props: NewCourseCardProps) {
  const { course, className } = props;

  const thumbnailUrl =
    course.coverPhotoV1 ||
    course.coverPhoto ||
    course.thumbnail ||
    getDefaultThumbnail(course);
  const thumbnail = () => (
    <Image alt="Thumbnail" src={thumbnailUrl} width={272} height={156} />
  );

  const handleOnClick = () => {
    const route = {
      href: getLinkPath(course),
      as: getLink(course)
    };
    pushRoute(route.href, route.as);
  };

  const { authorID } = courseAuthorText(course.authors[0]);
  const { color: topicColor } = getTopicGroupAssetObj(course.topic);

  return (
    <CourseCard
      as="div"
      className={className}
      thumbnail={thumbnail}
      href={getCourseLink(course)}
      title={course.name}
      langLabel={course.language}
      topicName={course.topic}
      topicColor={topicColor}
      educatorLink={authorID}
      detailsLine={getProfileCourseCardDetailsLine(course)}
      variant={course.isLive ? 'live' : null}
      onClick={handleOnClick}
      lessonTotalCount={course.itemCount}
      lessonCompletedCount={handleCompletedLessonCount(course)}
    />
  );
}

NewCourseCard.defaultProps = {
  className: ''
};

function courseAuthorText(author: IAuthor): {
  authorID: string;
  authorName: string;
} {
  let authorID = author.user?.relative_link;
  if (!authorID && author.username) authorID = `/@${author.username}`;
  const authorName = author.name;
  return { authorID, authorName };
}
